import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, useTheme } from '@mui/material';
import ModalWrapper from '../ModalWrapper';
import ButtonComponent from '../customButton';
import TableComponent from '../TableComponent';
import CustomChip from '../chips/CustomChip';
import { getSchedule } from '../../utils/api/schedule_apis';
import { STATUS } from '../../utils/api/sharedfunction';
import { dateFormat } from '../../utils/date';
import {
  formatScheduleChangeCadenceLines,
  formatScheduleChangeValue,
  getScheduleChangeCauseChipColors,
  getScheduleChangeTypeChipColors,
  getScheduleStatusChipColors,
} from '../../utils/scheduleDisplay';
import detailStyles from '../../styles/createTestLaunch/scheduleDetailModal.module.scss';

const {
  ScheduleChangeType,
  ScheduleChangeTypeLabel = {},
  ScheduleChangeCauseLabel = {},
  ScheduleStatus,
} = require('../../enums/ScheduleEnums');

const DEFAULT_PAGINATION = { page: 0, pageSize: 25 };

function ScheduleChangeLogModal({
  open,
  onClose,
  scheduleId,
}) {
  const theme = useTheme();
  const palette = theme.palette.customColor;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [scheduleName, setScheduleName] = useState('');
  const [changeRows, setChangeRows] = useState([]);
  const [paginationModel, setPaginationModel] = useState(DEFAULT_PAGINATION);

  useEffect(() => {
    if (!open || !scheduleId) return undefined;
    let cancelled = false;
    setError('');
    setLoading(true);
    setPaginationModel(DEFAULT_PAGINATION);
    getSchedule({ scheduleId })
      .then((res) => {
        if (cancelled) return;
        if (res.status !== STATUS.SUCCESS) {
          setError(res.error || 'Failed to load change log');
          setScheduleName('');
          setChangeRows([]);
          return;
        }
        const data = res.data || {};
        const sched = data.schedule || data;
        setScheduleName(sched?.name || '');
        const changes = data.changeHistory || data.change_history || [];
        setChangeRows(
          changes.map((row) => ({
            id: row.id,
            changed_at: row.changed_at,
            change_type: row.change_type,
            from_value: row.from_value,
            to_value: row.to_value,
            cause: row.cause,
            changed_by: row.changed_by_user?.username || null,
          }))
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, scheduleId]);

  const pagedChangeRows = useMemo(() => {
    const start = paginationModel.page * paginationModel.pageSize;
    return changeRows.slice(start, start + paginationModel.pageSize);
  }, [changeRows, paginationModel]);

  const renderStatusOrText = (changeType, raw, row, side) => {
    if (changeType === 'CADENCE') {
      const lines = formatScheduleChangeCadenceLines(raw);
      if (lines) {
        return (
          <div className={detailStyles.cadenceCell} title={[lines.primary, lines.secondary].filter(Boolean).join(' · ')}>
            <div className={detailStyles.cadencePrimary}>{lines.primary}</div>
            {lines.secondary ? (
              <div className={detailStyles.cadenceSecondary}>{lines.secondary}</div>
            ) : null}
          </div>
        );
      }
    }

    const isStatus = changeType === 'STATUS'
      || changeType === ScheduleChangeType?.STATUS;
    const isConfiguration = changeType === 'CONFIGURATION'
      || changeType === ScheduleChangeType?.CONFIGURATION;
    const label = formatScheduleChangeValue(changeType, raw, isConfiguration && row
      ? {
        compareTo: side === 'from' ? row.to_value : row.from_value,
      }
      : undefined);
    if (!isStatus || !raw) {
      return <span title={raw || ''}>{label}</span>;
    }
    const statusValues = ScheduleStatus ? Object.values(ScheduleStatus) : [];
    const knownStatus = statusValues.includes(raw);
    if (!knownStatus) {
      return <span title={raw || ''}>{label}</span>;
    }
    const colors = getScheduleStatusChipColors(palette, raw);
    return (
      <CustomChip
        label={String(raw).replace(/_/g, ' ')}
        color={colors.color}
        textColor={colors.textColor}
        borderColor={colors.borderColor}
        size="small"
      />
    );
  };

  const changeColumns = [
    {
      field: 'changed_at',
      headerName: 'When',
      flex: 1.1,
      minWidth: 150,
      renderCell: (params) => (params.value ? dateFormat(params.value) : '—'),
    },
    {
      field: 'change_type',
      headerName: 'Change',
      flex: 0.9,
      minWidth: 130,
      renderCell: (params) => {
        const label = (ScheduleChangeTypeLabel && ScheduleChangeTypeLabel[params.value])
          || params.value
          || '—';
        if (!params.value) return '—';
        const colors = getScheduleChangeTypeChipColors(palette, params.value);
        return (
          <CustomChip
            label={label}
            color={colors.color}
            textColor={colors.textColor}
            borderColor={colors.borderColor}
            size="small"
          />
        );
      },
    },
    {
      field: 'from_value',
      headerName: 'From',
      flex: 1.3,
      minWidth: 140,
      renderCell: (params) => renderStatusOrText(params.row.change_type, params.value, params.row, 'from'),
    },
    {
      field: 'to_value',
      headerName: 'To',
      flex: 1.3,
      minWidth: 140,
      renderCell: (params) => renderStatusOrText(params.row.change_type, params.value, params.row, 'to'),
    },
    {
      field: 'cause',
      headerName: 'Cause',
      flex: 1,
      minWidth: 130,
      renderCell: (params) => {
        const label = (ScheduleChangeCauseLabel && ScheduleChangeCauseLabel[params.value])
          || params.value
          || '—';
        if (!params.value) return '—';
        const colors = getScheduleChangeCauseChipColors(palette, params.value);
        return (
          <CustomChip
            label={label}
            color={colors.color}
            textColor={colors.textColor}
            borderColor={colors.borderColor}
            size="small"
          />
        );
      },
    },
    {
      field: 'changed_by',
      headerName: 'By',
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => (
        <span className={params.value ? undefined : detailStyles.muted}>
          {params.value || 'System'}
        </span>
      ),
    },
  ];

  const isChangeEmpty = !loading && changeRows.length === 0;

  return (
    <ModalWrapper
      isOpen={open}
      onClose={onClose}
      title={scheduleName ? `Change log — ${scheduleName}` : 'Change log'}
      size="large"
      className={detailStyles.modalChangeLog}
      bodyClassName={detailStyles.modalBody}
      footer={(
        <div className={detailStyles.footerBar}>
          <ButtonComponent
            ctaText="Close"
            variant="primary"
            className="white bg-blue600"
            onClick={onClose}
          />
        </div>
      )}
    >
      <div className={detailStyles.root}>
        {error && <Alert severity="error">{error}</Alert>}

        <Box className={detailStyles.tableSectionWide}>
          <TableComponent
            title="Schedule changes"
            headerActionButtons={[]}
            hideColumnsVisibilityButton
            rows={pagedChangeRows}
            columns={changeColumns}
            loading={loading}
            idField="id"
            isCheckbox={false}
            isRowSelection={false}
            isColumnResizable={!loading}
            isPagination
            paginationMode="server"
            rowCount={changeRows.length}
            paginationModel={paginationModel}
            handleCustomPaginationPageChange={(event, page) => (
              setPaginationModel((prev) => ({ ...prev, page }))
            )}
            handleCustomPaginationPageSizeChange={(pageSize) => (
              setPaginationModel({ page: 0, pageSize })
            )}
            isCustomPagination
            filterMode="client"
            sortingMode="client"
            tableHeight="480px"
            getRowHeight={() => 'auto'}
            getEstimatedRowHeight={() => 56}
            customStyles={{
              '& .MuiDataGrid-cell': {
                py: 1,
                display: 'flex',
                alignItems: 'center',
              },
            }}
            isData={!isChangeEmpty}
            customNoData={(
              <div className={detailStyles.emptyHistory}>
                <p className={detailStyles.emptyHistoryText}>
                  No change log entries for this schedule yet.
                </p>
              </div>
            )}
          />
        </Box>
      </div>
    </ModalWrapper>
  );
}

export default ScheduleChangeLogModal;
